/**
 * CareLogix™ ORACLE — Client-side MNPI/PHI heuristic mirror (Phase 26F).
 *
 * Mirror of `backend/oracle_healthcare_guardrails.py` to give the user
 * immediate feedback before the request hits the server. The server-side
 * filter is the source of truth.
 */

const MNPI_PATTERNS = [
  /insider tips?/i,
  /non[-\s]?public/i,
  /\bmnpi\b/i,
  /under embargo/i,
  /sealed (?:filing|study|document)/i,
  /confidential (?:filing|memo|preview)/i,
  /leaked (?:study|memo|filing|trial|results?|data)/i,
  /unannounced acquisition/i,
  /pre[-\s]?announcement/i,
  /pre[-\s]?release results?/i,
  /un[-\s]?published (?:phase\s?\d+|trial)/i,
  /\binternal email\b/i,
  /private placement memo/i,
  /\bdraft 10[-\s]?[kq]\b/i,
  /\bdraft 8[-\s]?k\b/i,
  /\bcomplete response letter\b/i,
  /\bcrl leak\b/i,
  /FDA decision not yet (?:public|announced)/i,
  /my (?:friend|contact|source) at (?:the )?FDA/i,
];

const PHI_PATTERNS = [
  /\bpatient (?:id|number|name)\b[:\s]*\S+/i,
  /\bmrn\b[:\s]*\S+/i,
  /\bmedical record (?:number)?\b[:\s]*\S+/i,
  /\bdate of birth\b/i,
  /\bdob\b/i,
  /\bssn\b\s*[:\-]?\s*\d{3}-?\d{2}-?\d{4}/i,
];

export const HEALTHCARE_MODES = new Set(["catalyst", "swing", "stocks", "options"]);

/**
 * @param {string} text
 * @returns {{ ok: boolean, kind?: "mnpi"|"phi", hits: string[], message: string } | { ok: true }}
 */
export function checkPublicOnly(text) {
  if (!text || typeof text !== "string") return { ok: true };
  const mnpiHits = [];
  const phiHits = [];
  for (const re of MNPI_PATTERNS) {
    const m = text.match(re);
    if (m) mnpiHits.push(m[0]);
  }
  for (const re of PHI_PATTERNS) {
    const m = text.match(re);
    if (m) phiHits.push(m[0]);
  }
  if (mnpiHits.length === 0 && phiHits.length === 0) return { ok: true };
  return {
    ok: false,
    kind: phiHits.length > 0 ? "phi" : "mnpi",
    hits: [...phiHits, ...mnpiHits].slice(0, 4),
    message:
      "Your message contains language that resembles material non-public " +
      "information (MNPI), confidential filings, or patient-identifying data. " +
      "CareLogix™ ORACLE uses public information only. Please rephrase using " +
      "public sources (FDA calendar, EDGAR, Form 4, Trials.gov).",
  };
}

export function isHealthcareMode(mode) {
  return HEALTHCARE_MODES.has((mode || "").toLowerCase());
}
